import { getBreakpoint, isSmallScreen } from "../../utils";
import { ToolbarItemProps } from "../components/toolbar-item.types";
import { ToolbarState } from "./toolbarContext.types";
import { ToolbarAction, ToolbarActionType } from "./toolbarReducer.types";

export const reducer = (
  state: ToolbarState,
  action: ToolbarAction
): ToolbarState => {
  switch (action.type) {
    case ToolbarActionType.REPLACE_STATE: {
      return {
        ...state,
        toolbarSchema: action.changedToolbarSchema,
      };
    }

    case ToolbarActionType.SET_ITEMS_WIDTH: {
      const newToolbarSchema = state.toolbarSchema.map((item, i) => {
        return {
          ...item,
          _privateProps: {
            ...item._privateProps,
            width: action.toolbarItemsWidth[i]?.width || 0,
          },
        };
      });

      const measured = !!action.toolbarItemsWidth?.[0]?.width;

      return {
        ...state,
        toolbarSchema: newToolbarSchema,
        measureInProgress: !measured,
      };
    }

    case ToolbarActionType.SET_ITEMS_VISIBILITY: {
      const padding = 6;
      const moreNavWidth = (action.showMoreButtonWidth || 48) + padding;
      let totalItemsWidth = 0;

      const toolbarItemsTotalWidth = state.toolbarSchema.reduce(
        (acc, item) => acc + (item._privateProps?.width || 0) + padding,
        0
      );
      const isMoreNavRequired =
        action.toolbarContainerWidth <= toolbarItemsTotalWidth;
      if (isMoreNavRequired) {
        totalItemsWidth += moreNavWidth;
      }

      const hasCustomPriority = state.toolbarSchema.some(
        (el) => el.priority !== 0
      );

      // Sort items by their priority to ensure higher-priority items are always placed into the visible area first
      const prioritySortedSchema = (): ToolbarItemProps[] =>
        [...state.toolbarSchema].sort(
          // We can assert the type because we protect its value while building a schema in toolbarItemsParser.ts
          (a, b) => (b.priority as number) - (a.priority as number)
        );

      const newToolbarSchema = (
        hasCustomPriority ? prioritySortedSchema() : state.toolbarSchema
      ).reduce((accumulator: ToolbarItemProps[], item) => {
        totalItemsWidth += item._privateProps?.width ?? 0;
        totalItemsWidth += padding;

        accumulator.push({
          ...item,
          _privateProps: {
            ...item._privateProps,
            /**
             * For small screen sizes we want all items to be hidden
             * since Spark v3 styles forces "visible" container to be hidden
             */
            visible:
              (!state.isSmallScreen || action.showToolbarItemsInSmallScreen) &&
              totalItemsWidth < action.toolbarContainerWidth,
          },
        });

        return accumulator;
      }, []);

      return {
        ...state,
        // If custom priority used - sort items back into their original order before inserting them into the document
        toolbarSchema: hasCustomPriority
          ? newToolbarSchema.sort(
              (a, b) => (a.order as number) - (b.order as number)
            )
          : newToolbarSchema,
        settingVisibilityInProgress: state.measureInProgress,
      };
    }

    case ToolbarActionType.CLEAR_MEASUREMENT: {
      const newToolbarSchema = state.toolbarSchema.map((item) => {
        return {
          ...item,
          _privateProps: {
            width: undefined,
            visible: undefined,
          },
        };
      });

      return {
        ...state,
        toolbarSchema: newToolbarSchema,
        measureInProgress: true,
        settingVisibilityInProgress: true,
      };
    }

    case ToolbarActionType.SET_IS_SMALL_SCREEN: {
      const computedBreakpoint = getBreakpoint(action.windowWidth);

      return {
        ...state,
        isSmallScreen: isSmallScreen(computedBreakpoint),
      };
    }

    case ToolbarActionType.CLOSE_SHOW_MORE_DROPDOWN: {
      return {
        ...state,
        closeShowMoreDropdown: action.inProgress,
      };
    }

    case ToolbarActionType.CLOSE_ALL_TOOLBAR_ITEM_DROPDOWNS: {
      return {
        ...state,
        closeAllToolbarItemDropdowns: action.inProgress,
      };
    }

    default:
      return state;
  }
};
